import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, useTheme, Flex, Image, VStack, AvatarGroup} from '@chakra-ui/react';
import OfferImage from '../../../../assets/images/offerImage.png';
import {TextG10, TextG12, TextG14, TextG16, TextG8, TextG18, TextG20} from '../../../typography';
import {Offer, OfferType, Place, Event} from '../../../../types/article';
import Card from '../../../Card';
import {getOfferDate, isFutureOffer} from '../../../../utils/date';
import useBookmark from '../../../../hooks/useBookmark';
import {getImageURL} from '../../../../constants/imageStorage';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../../hooks/useVisible';
import {has} from 'lodash';
import dayjs from 'dayjs';
import {useSearchParams} from 'react-router-dom';
import CardInterestTags from '../../CardInterestTags';
import {BookmarkElement} from '../../index';
import useOfferModal from '../../../../hooks/useOfferModal';
import styled from 'styled-components';
import Icon from '../../../Icon';
import {useFetchEvent} from '../../../../hooks/useEvent';
import ExclusiveCommunityLabel from '../../../ExclusiveCommunityLabel';
import useTrackEvent from '../../../../hooks/useTrackEvent';
import useAuth from '../../../../hooks/useAuth';
import {UsersListModal} from '../../../UsersList';
import {useFetchOfferBookmarkedUsers} from '../../../../hooks/useOffers';
import UserAvatar from '../../../UserAvatar';

interface OfferCardProps {
  offer?: any;
  fullCard?: boolean;
  rightBottomItem?: any;
  place?: Place;
  event?: Event;
  hideActive?: boolean;
  hideCard?: boolean;
  interestsScrollable?: boolean;
  height?: string;
  isFeatured?: boolean;
  showExclusive?: boolean;
  showBookmark?: boolean;
}

export const getOfferCardColor = (offerType: OfferType) => {
  switch (offerType) {
    case OfferType.INDIVIDUAL_REDEMPTION:
      return '#329241';
    case OfferType.REDEMPTION:
      return '#F5737F';
    case OfferType.PREMIUM_REDEMPTION:
      return '#E02F84';
    case OfferType.POINTS_BOOST:
      return '#8A44E3';
    case OfferType.EVENT_REDEMPTION:
      return '#272929';
    default:
      return '#8A44E3';
  }
};

const OverflowDiv = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  z-index: 99;
  border-radius: 10px;
  display: flex;
  cursor: pointer;
  -webkit-padding-start: var(--chakra-space-3);
  padding-inline-start: var(--chakra-space-3);
  -webkit-padding-end: var(--chakra-space-3);
  padding-inline-end: var(--chakra-space-3);
  padding-top: var(--chakra-space-3);
  padding-bottom: var(--chakra-space-3);
  justify-content: center;
`;

const StyledTitle = styled(TextG14)`
  font-size: ${(props) => (props.$isFeatured ? '18px !important' : '14px')};
  font-weight: ${(props) => (props.$isFeatured ? '700' : '500')};
`;

const SpecialForYouBadge = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: #e02f84;
  color: white;
  padding: 5px 18px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
`;

const MembershipLockOverlay = styled.div<{$isExclusive?: boolean}>`
  position: absolute;
  top: ${(props) => (props.$isExclusive ? '6px' : '0')};
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  border-radius: 10px;
  display: flex;
  cursor: pointer;
  -webkit-padding-start: var(--chakra-space-3);
  padding-inline-start: var(--chakra-space-3);
  -webkit-padding-end: var(--chakra-space-3);
  padding-inline-end: var(--chakra-space-3);
  padding-top: var(--chakra-space-3);
  padding-bottom: var(--chakra-space-3);
  justify-content: center;
`;

export const checkIsOfferLocked = (offerId: number, event?: Event, offer?: Offer, user?: any) => {
  // Check event-based locking first
  let isEventLocked = false;
  if (event?.unlocks_offer_id === offerId) {
    if (event?.unlocked_offer) {
      isEventLocked = !(
        event?.unlocked_offer.offer_id === offerId && event?.unlocks_offer_id === offerId
      );
    } else {
      isEventLocked = true;
    }
  }

  // Check membership-based locking
  let isMembershipLocked = false;
  if (offer) {
    // If offer is private and has exclusivity, check membership
    if (offer.is_private && offer.community_membership_plan?.has_offer_exclusivity) {
      // A user individually granted access is never membership-locked.
      isMembershipLocked = !user?.hasActiveMembership && !offer.has_individual_access;
    }
  }
  // Determine lock type
  let lockType: 'event' | 'membership' | null = null;
  if (isEventLocked) {
    lockType = 'event';
  } else if (isMembershipLocked) {
    lockType = 'membership';
  }

  return {
    isLocked: isEventLocked || isMembershipLocked,
    isEventLocked,
    isMembershipLocked,
    lockType,
  };
};

const PremiumOfferCard = ({
  offer,
  rightBottomItem,
  place,
  event,
  hideCard = false,
  height,
  isFeatured = false,
  showExclusive = false,
  showBookmark = true,
}: OfferCardProps) => {
  if (!offer || offer.offer_type === undefined) return null;
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const title = useRef<HTMLDivElement>(null);
  const [hide, setHide] = useState(hideCard);
  const {isBookmarked, handleOnBookmarkClick} = useBookmark(offer as Offer, 'offer');
  const {showOfferModal} = useOfferModal();
  const {addUserEvent} = useTrackEvent();
  const {user} = useAuth();
  const isExclusive = offer?.communities?.length > 0 && offer.is_private;
  const [offerEvent, setOfferEvent] = useState<Event | undefined>(event);
  const [lockInfo, setLockInfo] = useState(checkIsOfferLocked(offer.id, offerEvent, offer, user));
  const [showUsersModal, setShowUsersModal] = useState(false);
  const bookmarksCount = offer?.bookmarks_count || 0;
  const {data: bookmarkedUsersData} = useFetchOfferBookmarkedUsers(offer.id, bookmarksCount > 0);
  const bookmarkedUsers = bookmarkedUsersData?.pages[0]?.data?.data || [];

  const {data: lazyLoadadedEvent} = offer?.event_id ? useFetchEvent(offer.event_id) : {data: null};

  useEffect(() => {
    if (
      (!offerEvent ||
        (offerEvent && has(offerEvent, 'unlocks_offer_id') && has(offerEvent, 'unlocked_offer'))) &&
      lazyLoadadedEvent
    ) {
      const fetchedOffer = lazyLoadadedEvent?.data;
      setOfferEvent(fetchedOffer);
      setLockInfo(checkIsOfferLocked(offer.id, fetchedOffer, offer, user));
    }
  }, [lazyLoadadedEvent, user]);
  // Update locked state when user changes
  useEffect(() => {
    setLockInfo(checkIsOfferLocked(offer.id, offerEvent, offer, user));
  }, [user?.hasActiveMembership, offer.id, offerEvent, offer]);

  const datePassed = useMemo(() => dayjs().utc().isAfter(dayjs.utc(offer?.ends_at)), [offer]);
  const futureOffer = useMemo(() => isFutureOffer(offer), [offer]);

  const [ref, {wasEverVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();

  useEffect(() => {
    if (!!searchParams.get('offer') && searchParams.get('offer') == offer.id.toString()) {
      showOfferModal({offer});
      if (!!searchParams.get('bookmark') && !isBookmarked) {
        handleOnBookmarkClick(null);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (wasEverVisible && offer?.id.toString()) {
      addVisibleArticle({id: offer.id.toString(), type: 'offer'});
    }
  }, [wasEverVisible]);

  const offerExpireDateString =
    !getOfferDate(offer?.starts_at) && !getOfferDate(offer?.ends_at)
      ? null
      : futureOffer
      ? `Available  ${
          getOfferDate(offer?.starts_at)?.includes('Tomorrow') ||
          getOfferDate(offer?.starts_at)?.includes('Today')
            ? getOfferDate(offer?.starts_at)?.toLocaleLowerCase()
            : `on ${getOfferDate(offer?.starts_at)}`
        }`
      : `${datePassed ? 'Expired ' : 'Expires '} ${
          getOfferDate(offer?.ends_at)?.includes('Tomorrow') ||
          getOfferDate(offer?.ends_at)?.includes('Today')
            ? getOfferDate(offer?.ends_at)?.toLocaleLowerCase()
            : `${getOfferDate(offer?.ends_at)}`
        }`;

  const offerTypePoints = ((offer?.offer_type == OfferType.POINTS_BOOST &&
    offer?.points_earning !== null) ||
    offer?.offer_type !== OfferType.POINTS_BOOST) && (
    <Box
      py={1}
      px={1}
      pos="absolute"
      top={offer?.offer_type == OfferType.POINTS_BOOST ? 0 : ''}
      left={offer?.offer_type == OfferType.POINTS_BOOST ? 0 : ''}
      bottom={offer?.offer_type == OfferType.POINTS_BOOST ? '' : 0}
      right={offer?.offer_type == OfferType.POINTS_BOOST ? '' : 0}
      borderTopLeftRadius="10px"
      borderBottomRightRadius="10px"
      background={offer?.offer_type == OfferType.POINTS_BOOST ? 'green' : theme.colors.rose}>
      <Flex>
        <TextG10
          fontWeight="700"
          color={offer?.offer_type == OfferType.POINTS_BOOST ? 'white' : theme.colors.white}>
          {offer?.offer_type == OfferType.POINTS_BOOST
            ? `+${offer?.points_earning.toLocaleString()}`
            : offer?.points_redeem_amount.toLocaleString() == '0'
            ? 'Free'
            : offer?.points_redeem_amount.toLocaleString()}
        </TextG10>
      </Flex>
    </Box>
  );

  const offerCardContent = isFeatured ? (
    <Card
      article={offer}
      childTop={isFeatured ? `calc(77% - ${(title.current?.clientHeight || 24) / 2}px)` : ''}
      overlayBackgroundColor={isFeatured ? '#2729295C' : ''}
      overlayHeight={isFeatured ? '33%' : ''}
      borderRadius={isFeatured ? '0px' : '10px'}
      height={height}
      backgroundColor={offer?.offer_type && getOfferCardColor(offer.offer_type)}
      childPaddingX={4}>
      <VStack alignItems={'left'} ref={title}>
        <StyledTitle
          wordBreak="break-word"
          fontWeight="700"
          color={theme.colors.white}
          noOfLines={2}
          $isFeatured={isFeatured}>
          {offer?.title}
        </StyledTitle>
        {futureOffer ? (
          <TextG10 fontWeight="700" color={theme.colors.white} isTruncated="&hellip;" noOfLines={1}>
            Available {getOfferDate(offer?.starts_at)}
          </TextG10>
        ) : offer.offerType == OfferType.MEMBERSHIP_GET || OfferType.MEMBERSHIP_GIVE ? (
          <></>
        ) : (
          <TextG10 fontWeight="700" color={theme.colors.white} isTruncated="&hellip;" noOfLines={1}>
            {`${datePassed ? 'Expired on' : 'Expires'} ${getOfferDate(offer?.ends_at)}`}
          </TextG10>
        )}
      </VStack>
    </Card>
  ) : (
    <>
      {!hide && (
        <>
          {isExclusive && showExclusive ? (
            <ExclusiveCommunityLabel community={offer.communities[0]} />
          ) : null}{' '}
          {/* Fixed typo from showExcluse to showExclusive */}
          <Box
            position="relative"
            ref={ref}
            px="3px"
            pt="3px"
            borderRadius="10px"
            background={offer?.offer_type && getOfferCardColor(offer.offer_type)}
            height={'auto'}
            width="100%"
            onClick={() => {
              showOfferModal({offer});

              addUserEvent({
                type: 'click',
                event_name: 'card_click',
                event_time: dayjs().format(),
                data: {itemType: 'offer', itemId: offer.id},
              });
            }}>
            {/* Sold out overlay */}
            {offer?.quantity_available === 0 && (
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                backgroundColor="rgba(0, 0, 0, 0.75)"
                borderRadius="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex="10">
                <Box
                  background="linear-gradient(135deg, #6c757d, #5a6268)"
                  px="4"
                  py="2"
                  borderRadius="8px"
                  border="2px solid white"
                  boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)">
                  <TextG12 
                    fontWeight="800" 
                    color="white"
                    textShadow="1px 1px 2px rgba(0, 0, 0, 0.5)"
                    letterSpacing="1px">
                    UNAVAILABLE
                  </TextG12>
                </Box>
              </Box>
            )}
            <Box maxH="100px" flex="none" pos="relative">
              <Image
                fallbackStrategy="onError"
                w={'100%'}
                borderRadius="10px"
                objectFit="cover"
                h="100px"
                src={offer?.image_url ? getImageURL(offer.image_url) : OfferImage}
              />
              {offer?.retail_price && parseFloat(offer.retail_price) > 0 && (
                <Box
                  py={1}
                  px={1.5}
                  pos="absolute"
                  top={0}
                  left={0}
                  borderTopLeftRadius="10px"
                  borderBottomRightRadius="10px"
                  background="rgba(255, 255, 255, 0.95)"
                  boxShadow="0 2px 4px rgba(0,0,0,0.2)">
                  <TextG10 fontWeight="700" color="#272929">
                    ${parseFloat(offer.retail_price).toFixed(0)} value
                  </TextG10>
                </Box>
              )}
              {offer.offerType == OfferType.MEMBERSHIP_GET || OfferType.MEMBERSHIP_GIVE ? (
                <></>
              ) : offerExpireDateString ? (
                <Box
                  px={'9px'}
                  py={'6px'}
                  position="absolute"
                  bottom="10px"
                  left="12px"
                  backgroundColor="#FFFFFF"
                  borderRadius="12px">
                  <TextG8 fontWeight="500">{offerExpireDateString}</TextG8>
                </Box>
              ) : null}
              {offerTypePoints}
            </Box>
            <Flex gap={2} w="100%" h="100%" userSelect="none" flexDir={'row'}>
              <VStack
                align="flex-start"
                color={theme.colors.white}
                py={3}
                px={2}
                position="relative"
                flex="1">
                <TextG14
                  textAlign="left"
                  pr={'36px'}
                  wordBreak="break-word"
                  fontWeight="700"
                  color={theme.colors.white}
                  noOfLines={2}>
                  {offer?.title}
                </TextG14>

                {offerEvent && offerEvent.unlocks_offer_id !== null ? (
                  <TextG10 noOfLines={2} color={theme.colors.white}>
                    {offerEvent.title}
                  </TextG10>
                ) : offer?.place ? (
                  <TextG10 noOfLines={2} color={theme.colors.white}>
                    {offer.place.name}
                  </TextG10>
                ) : place?.name ? (
                  <TextG10 noOfLines={2} color={theme.colors.white}>
                    {place.name}
                  </TextG10>
                ) : null}
                {bookmarksCount > 0 && (
                  <Flex
                    align="center"
                    gap={2}
                    alignSelf="flex-start"
                    cursor="pointer"
                    _hover={{opacity: 0.8}}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowUsersModal(true);
                    }}>
                    <AvatarGroup size="xs" max={3} spacing={-2}>
                      {bookmarkedUsers.slice(0, 3).map((user: any, index: number) => (
                        <UserAvatar
                          key={user.user_id || `user-${index}`}
                          user={{
                            id: user.user_id,
                            name: user.name,
                            surname: user.surname,
                            handle: user.handle,
                            profilePhotoUrl: user.profile_photo_url,
                          } as any}
                          size="xs"
                          avatar_image={user.profile_photo_url || undefined}
                          sx={{
                            border: '1.5px solid white',
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      ))}
                    </AvatarGroup>
                    <TextG10
                      color={theme.colors.white}
                      fontWeight="500"
                      _hover={{textDecoration: 'underline'}}>
                      {`${bookmarksCount} Neighbor${bookmarksCount !== 1 ? 's' : ''} Interested`}
                    </TextG10>
                  </Flex>
                )}
              </VStack>
              {rightBottomItem && rightBottomItem}
              {showBookmark && <BookmarkElement article={{...offer, type: 'offer'}} />}
            </Flex>
          </Box>
        </>
      )}
    </>
  );

  const showSpecialForYouBadge = !!offer?.has_individual_access && !lockInfo.isLocked;

  return (
    <Box w={'100%'} pos={'relative'} zIndex={10}>
      {lockInfo.isLocked && (
        <>
          {lockInfo.lockType === 'membership' ? (
            <MembershipLockOverlay
              $isExclusive={isExclusive}
              onClick={() => showOfferModal({offer})}>
              <VStack
                mt="25px"
                display="flex"
                pos="relative"
                alignItems={'center'}
                justifyContent={'flex-start'}>
                <Icon iconName="fi fi-sr-lock" style={{color: 'white', fontSize: '30px'}} />
                <TextG20 color={'white'} fontWeight={'700'} mt={0}>
                  Special Perk for Insiders
                </TextG20>
                <TextG18 color={'white'} fontWeight={'400'} mt={1} textAlign={'center'}>
                  Insiders use perks as a reason to get together out in the neighborhood.
                </TextG18>
                <TextG18 color={'white'} fontWeight={'400'} mt={1} textAlign={'center'}>
                  Join the Insiders Club to unlock even more connection.
                </TextG18>
              </VStack>
            </MembershipLockOverlay>
          ) : (
            <OverflowDiv onClick={() => showOfferModal({offer})}>
              <VStack display="flex" pos="relative" alignItems={'center'} justifyContent={'center'}>
                <Icon iconName="fi fi-sr-lock" style={{color: 'white', fontSize: '30px'}} />
                <TextG14 color={'white'} fontWeight={'700'} mt={0}>
                  Locked
                </TextG14>
                <TextG12 color={'white'} fontWeight={'400'} mt={1} textAlign={'center'}>
                  Unlocked with Check-In
                </TextG12>
              </VStack>
            </OverflowDiv>
          )}
        </>
      )}
      {showSpecialForYouBadge && (
        <SpecialForYouBadge onClick={() => showOfferModal({offer})}>
          Special for you
        </SpecialForYouBadge>
      )}
      {offerCardContent}
      <UsersListModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        title="Neighbors Interested"
        totalCount={bookmarksCount}
        fetchUsersHook={useFetchOfferBookmarkedUsers}
        entityId={offer.id}
      />
    </Box>
  );
};

export default PremiumOfferCard;
